import type { LucideIcon } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useRef } from "react";
import { useLocation, useSearchParams } from "react-router";

import { TAB_ICON_CLASS, tabClassName } from "@/components/page-tabs";

/** Overzicht's two tables: per tool (the default) and per person. */
export const OVERVIEW_TABLES = ["tools", "personen"] as const;

export type OverviewTable = (typeof OVERVIEW_TABLES)[number];

/** The search parameter that names the table Overzicht shows: `?table=personen`. */
export const TABLE_PARAM = "table";

/** The table a search names (`?table=personen`); the tools for any other or none. */
export function parseOverviewTable(value: string | null): OverviewTable {
  return OVERVIEW_TABLES.find((table) => table === value) ?? OVERVIEW_TABLES[0];
}

/**
 * The table Overzicht shows, kept in its URL (`?table=personen`, left out for
 * the tools), so a link lands on the right one. A switch replaces the history
 * entry, as the period and the filters do, and leaves every other parameter:
 * the period and each table's own sort.
 */
export function useOverviewTable(): [OverviewTable, (table: OverviewTable) => void] {
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  return [
    parseOverviewTable(params.get(TABLE_PARAM)),
    (table) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (table === OVERVIEW_TABLES[0]) {
            next.delete(TABLE_PARAM);
          } else {
            next.set(TABLE_PARAM, table);
          }
          return next;
        },
        { replace: true, state },
      ),
  ];
}

/** One of the tabs: its value, its label in the dashboard's language and its icon. */
export interface TableTab<T extends string> {
  value: T;
  label: string;
  icon: LucideIcon;
  /** The table it shows. */
  panel: ReactNode;
}

interface TableTabsProps<T extends string> {
  tabs: readonly TableTab<T>[];
  value: T;
  onChange: (value: T) => void;
  /** The tab list's name for screen readers: "Tabellen". */
  label: string;
  /** Makes the tabs' and panels' ids unique on the page: "overview". */
  id: string;
}

/** What the arrow keys and Home and End move to, from a tab. */
const STEPS: Record<string, (index: number, count: number) => number> = {
  ArrowLeft: (index, count) => (index - 1 + count) % count,
  ArrowRight: (index, count) => (index + 1) % count,
  Home: () => 0,
  End: (_, count) => count - 1,
};

/**
 * A choice between a few tables in the same place, as tabs in the look of the
 * page's own tabs (`<PageTabs>`): each with its icon (hidden on a phone), the
 * open one in the blue tint. Only the open table shows; the others stay
 * mounted but hidden, so each keeps its own search and filters across a
 * switch. A tab list for the keyboard: one Tab stop, the arrows (and Home and
 * End) move to the next tab and open it.
 *
 *     <TableTabs
 *       id="overview"
 *       label="Tabellen"
 *       tabs={[
 *         { value: "tools", label: "Per tool", icon: Wrench, panel: <ToolTable … /> },
 *         { value: "personen", label: "Per persoon", icon: UserRound, panel: <PersonTable … /> },
 *       ]}
 *       value={table}
 *       onChange={setTable}
 *     />
 */
export function TableTabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  id,
}: TableTabsProps<T>) {
  const list = useRef<HTMLDivElement>(null);
  const tabId = (tab: T) => `${id}-tab-${tab}`;
  const panelId = (tab: T) => `${id}-panel-${tab}`;

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = STEPS[event.key];
    const nextIndex = step ? step(index, tabs.length) : -1;
    const next = tabs[nextIndex];
    if (!next) {
      return;
    }
    event.preventDefault();
    onChange(next.value);
    list.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIndex]?.focus();
  };

  return (
    <div>
      <div
        ref={list}
        role="tablist"
        aria-label={label}
        className="mb-3 flex flex-wrap items-center gap-1 max-sm:gap-0.5"
      >
        {tabs.map((tab, index) => {
          const selected = tab.value === value;
          const Icon = tab.icon;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              id={tabId(tab.value)}
              aria-selected={selected}
              aria-controls={panelId(tab.value)}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(tab.value)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={tabClassName(selected)}
            >
              <Icon className={TAB_ICON_CLASS} aria-hidden="true" />
              {tab.label}
            </button>
          );
        })}
      </div>
      {tabs.map((tab) => (
        <div
          key={tab.value}
          role="tabpanel"
          id={panelId(tab.value)}
          aria-labelledby={tabId(tab.value)}
          hidden={tab.value !== value}
        >
          {tab.panel}
        </div>
      ))}
    </div>
  );
}
